// Iter 128 — Currency picker dropdown.  Used in EarningsPage.
// Persists to backend via PATCH /api/me/currency for logged-in users.
import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { CURRENCIES, getCurrency } from "../lib/currency";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Coins, Check } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const CurrencyPicker = ({ value, onChange, className = "" }) => {
  const { user, refreshMe } = useAuth();
  const [saving, setSaving] = useState(false);
  const current = getCurrency(value);

  const handlePick = async (code) => {
    if (code === current.code) return;
    onChange?.(code);
    if (!user) return;            // anonymous → local state only
    setSaving(true);
    try {
      await axios.patch(`${API}/me/currency`, { currency: code });
      await refreshMe?.();
      toast.success(`Currency set to ${code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save currency");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="currency-picker-trigger"
        disabled={saving}
        className={`inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-3.5 h-9 text-xs font-bold uppercase tracking-[0.14em] transition ${className}`}
      >
        <Coins className="w-3.5 h-3.5 text-emerald-400" />
        <span>{current.flag}</span>
        <span>{current.code}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#12161d] border-white/[0.08] min-w-[220px]"
        data-testid="currency-picker-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
          Display currency
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/[0.06]" />
        {CURRENCIES.map((c) => (
          <DropdownMenuItem
            key={c.code}
            data-testid={`currency-option-${c.code}`}
            onSelect={() => handlePick(c.code)}
            className="cursor-pointer flex items-center justify-between gap-3 py-2 focus:bg-white/[0.06]"
          >
            <div className="flex items-center gap-2.5">
              <span className="text-base leading-none">{c.flag}</span>
              <div>
                <div className="text-sm font-bold leading-tight">{c.code}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{c.name}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-tabular">{c.symbol}</span>
              {c.code === current.code && <Check className="w-3.5 h-3.5 text-emerald-400" />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CurrencyPicker;
